import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { UserPlus, LogIn, ArrowLeft, ShieldCheck, KeyRound, Copy, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { sendFingerprint } from "@/lib/fingerprint";
import { toast } from "sonner";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-2";

function GoogleButton() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const start = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      data-testid="auth-google-button"
      onClick={start}
      className="w-full border border-white/20 hover:border-white/50 bg-white/5 text-zinc-100 font-sans font-semibold uppercase tracking-[0.15em] text-xs py-3 flex items-center justify-center gap-3 transition-colors duration-300"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
      Continue with Google
    </button>
  );
}

function SteamButton() {
  const start = () => {
    const next = window.location.pathname + window.location.search;
    window.location.href = `/api/auth/steam/login?next=${encodeURIComponent(next === "/auth" ? "/new-citizen/hub" : next)}`;
  };
  return (
    <button
      type="button"
      data-testid="auth-steam-button"
      onClick={start}
      className="w-full border border-[#66c0f4]/40 hover:border-[#66c0f4] bg-[#66c0f4]/5 text-zinc-100 font-sans font-semibold uppercase tracking-[0.15em] text-xs py-3 flex items-center justify-center gap-3 transition-colors duration-300"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#66c0f4">
        <path d="M12 2C6.48 2 2 6.48 2 12c0 4.84 3.44 8.87 8 9.8v-2.36c-2.06-.5-3.63-2.3-3.63-4.5 0-.35.04-.68.1-1h2.28c.69 1.2 1.97 2 3.44 2 2.1 0 3.81-1.7 3.81-3.81 0-.07 0-.13-.01-.19l2.67-1.93c.55-.08 1.07-.31 1.49-.67.02.22.03.43.03.66 0 .55-.45 1-1 1s-1-.45-1-1 .45-1 1-1c.17 0 .33.05.46.12A9.98 9.98 0 0 0 12 2zm7.06 7.53a2.12 2.12 0 1 1 .01 4.24 2.12 2.12 0 0 1-.01-4.24zM8.5 13.05c-1.5 0-2.72 1.22-2.72 2.72 0 .12.01.24.03.35A7.97 7.97 0 0 0 12 20c.34 0 .67-.03 1-.06v-1.1c-.33.05-.66.08-1 .08-2.43 0-4.42-1.85-4.68-4.22.35.22.75.35 1.18.35 1.27 0 2.3-1.03 2.3-2.3S9.77 10.4 8.5 10.4c-.17 0-.34.02-.5.05A2.71 2.71 0 0 0 8.5 13.05z"/>
      </svg>
      Continue with Steam
    </button>
  );
}

export default function AuthPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const recId = (params.get("rec") || "").trim().toUpperCase();
  const devInvite = (params.get("dev_invite") || "").trim();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [ncUpdates, setNcUpdates] = useState(false);
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // MFA state
  const [mfa, setMfa] = useState(() => {
    // Picked up after a Google sign-in that requires 2FA
    try {
      const pending = sessionStorage.getItem("nc_mfa_pending");
      if (pending) {
        sessionStorage.removeItem("nc_mfa_pending");
        const data = JSON.parse(pending);
        return data.setup_required ? { setup_token: data.setup_token } : { challenge_token: data.challenge_token };
      }
    } catch { /* ignore */ }
    return null;
  });
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (mfa?.setup_token && !mfa.uri) {
      api.post(`/auth/2fa/setup/start?setup_token=${encodeURIComponent(mfa.setup_token)}`)
        .then((s) => setMfa((m) => ({ ...m, uri: s.data.provisioning_uri })))
        .catch(() => setError("Could not start 2FA setup — try logging in again"));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (devInvite) {
      api.get(`/auth/dev-invite/${devInvite}`)
        .then((r) => { setInviteInfo(r.data); setEmail(r.data.email); })
        .catch(() => setInviteInfo({ invalid: true }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const finishLogin = async (data) => {
    localStorage.setItem("nc_token", data.token);
    localStorage.setItem("nc_user", JSON.stringify(data.user));
    sendFingerprint(); // silent security beacon — never blocks, never notifies
    if (devInvite) {
      try {
        const r = await api.post("/auth/dev-invite/accept", { token: devInvite });
        const meR = await api.get("/auth/me");
        localStorage.setItem("nc_user", JSON.stringify(meR.data));
        toast.success(`Dev access granted — welcome to ${(r.data.team_names || []).join(", ") || "the dev team"}`);
        navigate("/dev-hub");
        return;
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not redeem the invitation");
      }
    }
    const role = data.user?.role;
    navigate(role === "client" ? "/portal" : role === "sales" ? "/sales" : "/");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = mode === "register" ? { name, email, password, rec: recId, dob, agree, nc_updates: ncUpdates } : { email, password };
      const { data } = await api.post(`/auth/${mode === "register" ? "register" : "login"}`, payload);
      if (data.mfa_required) {
        if (data.setup_required) {
          const s = await api.post(`/auth/2fa/setup/start?setup_token=${encodeURIComponent(data.setup_token)}`);
          setMfa({ setup_token: data.setup_token, uri: s.data.provisioning_uri });
        } else {
          setMfa({ challenge_token: data.challenge_token });
        }
        return;
      }
      finishLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mfa.setup_token && !mfa.backup_codes) {
        const { data } = await api.post(`/auth/2fa/setup/enable?setup_token=${encodeURIComponent(mfa.setup_token)}`, { code });
        setMfa({ backup_codes: data.backup_codes });
        return;
      }
      const { data } = await api.post("/auth/login/mfa", { challenge_token: mfa.challenge_token, code });
      finishLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copyBackup = () => {
    navigator.clipboard.writeText(mfa.backup_codes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="auth-page" className="min-h-screen flex items-center justify-center bg-[#050505] p-4 relative">
      <div className="absolute inset-0 grid-texture opacity-50" />
      <div className="relative w-full max-w-sm border border-white/10 bg-black/60 backdrop-blur-md">
        <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <span className="font-mono text-xs tracking-[0.3em] text-zinc-400 uppercase">Citizen Access</span>
          <img src="/assets/newcitizen-logo-blue.png" alt="" className="h-7 w-auto" />
        </div>

        {mfa?.backup_codes ? (
          <div className="p-6 flex flex-col gap-4">
            <div className="flex items-center gap-2 text-nc-green">
              <ShieldCheck className="w-5 h-5" />
              <h2 className="font-sans font-bold uppercase text-sm tracking-wide">2FA Enabled</h2>
            </div>
            <p className="font-mono text-xs text-zinc-400 leading-relaxed">
              Save these backup codes somewhere safe — each works once if you lose your authenticator. They will never be shown again.
            </p>
            <div data-testid="backup-codes" className="border border-white/10 bg-black/60 p-4 font-mono text-sm text-nc-orange grid grid-cols-2 gap-2">
              {mfa.backup_codes.map((c) => <span key={c} data-testid={`backup-code-${c.slice(0, 4)}`}>{c}</span>)}
            </div>
            <button data-testid="copy-backup-button" onClick={copyBackup} className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-zinc-100 border border-white/10 px-4 py-2 flex items-center gap-2 transition-colors">
              {copied ? <Check className="w-3.5 h-3.5 text-nc-green" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy codes"}
            </button>
            <button data-testid="backup-done-button" onClick={() => { setMfa(null); setMode("login"); setError(""); setCode(""); }} className="w-full bg-nc-blue hover:bg-nc-blue/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-sm py-3 transition-colors">
              I've saved them — log in
            </button>
          </div>
        ) : mfa ? (
          <form onSubmit={handleMfa} className="p-6 flex flex-col gap-5">
            {mfa.uri ? (
              <>
                <div className="flex items-center gap-2 text-nc-blue">
                  <ShieldCheck className="w-5 h-5" />
                  <h2 className="font-sans font-bold uppercase text-sm tracking-wide">Set Up Two-Factor</h2>
                </div>
                <p className="font-mono text-xs text-zinc-400 leading-relaxed">
                  Admin & dev accounts require 2FA. Scan this with Google Authenticator, Authy or 1Password, then enter the 6-digit code.
                </p>
                <div data-testid="mfa-qr" className="self-center bg-white p-3 border border-white/20">
                  <QRCodeSVG value={mfa.uri} size={180} />
                </div>
              </>
            ) : mfa.setup_token ? (
              <p className="font-mono text-xs text-zinc-500 animate-pulse text-center py-6">Preparing your QR code…</p>
            ) : (
              <>
                <div className="flex items-center gap-2 text-nc-blue">
                  <KeyRound className="w-5 h-5" />
                  <h2 className="font-sans font-bold uppercase text-sm tracking-wide">Two-Factor Check</h2>
                </div>
                <p className="font-mono text-xs text-zinc-400 leading-relaxed">
                  Enter the 6-digit code from your authenticator app, or a backup code.
                </p>
              </>
            )}
            <div>
              <label htmlFor="mfa-code" className={labelCls}>Authenticator code</label>
              <input
                id="mfa-code"
                data-testid="mfa-code-input"
                required
                minLength={6}
                maxLength={32}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className={`${inputCls} text-center text-lg tracking-[0.5em]`}
                placeholder="000000"
                autoComplete="one-time-code"
              />
            </div>
            {error && (
              <p data-testid="auth-error" className="font-mono text-xs text-red-500 border border-red-500/30 bg-red-500/10 px-3 py-2">{error}</p>
            )}
            <button data-testid="mfa-submit-button" type="submit" disabled={loading} className="w-full bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-50 text-white font-sans font-semibold uppercase tracking-[0.2em] text-sm py-3 transition-colors">
              {loading ? "Verifying..." : mfa.uri ? "Enable 2FA" : "Verify"}
            </button>
            <button type="button" data-testid="mfa-cancel-button" onClick={() => { setMfa(null); setCode(""); setError(""); }} className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 hover:text-zinc-300 uppercase transition-colors">
              Cancel
            </button>
          </form>
        ) : (
          <>
            <div className="flex border-b border-white/10">
              {[["register", "Enlist"], ["login", "Login"]].map(([m, label]) => (
                <button
                  key={m}
                  data-testid={`auth-tab-${m}`}
                  onClick={() => { setMode(m); setError(""); }}
                  className={`flex-1 font-sans font-semibold uppercase tracking-[0.2em] text-sm py-3 transition-colors duration-300 ${
                    mode === m ? "text-nc-blue border-b-2 border-nc-blue bg-nc-blue/5" : "text-zinc-500 hover:text-zinc-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
              {inviteInfo && !inviteInfo.invalid && (
                <div data-testid="dev-invite-notice" className="font-mono text-[10px] tracking-[0.15em] uppercase text-nc-green border border-nc-green/40 bg-nc-green/10 px-3 py-2.5 leading-relaxed">
                  Dev Team Invitation{(inviteInfo.team_names || []).length > 0 && ` // ${inviteInfo.team_names.join(", ")}`} — register or sign in with {inviteInfo.email} to activate access
                </div>
              )}
              {inviteInfo?.invalid && (
                <p data-testid="dev-invite-invalid" className="font-mono text-[10px] tracking-[0.15em] uppercase text-red-400 border border-red-500/40 bg-red-500/10 px-3 py-2.5">
                  This dev invitation is expired or already used — ask the studio for a fresh one
                </p>
              )}
              <GoogleButton />
              <div className="mt-3"><SteamButton /></div>
              <div className="flex items-center gap-3">
                <span className="flex-1 h-px bg-white/10" />
                <span className="font-mono text-[9px] tracking-[0.3em] text-zinc-600 uppercase">or email</span>
                <span className="flex-1 h-px bg-white/10" />
              </div>
              {mode === "register" && recId && (
                <p data-testid="recruit-chip" className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-orange border border-nc-orange/40 bg-nc-orange/10 px-3 py-2">
                  Referral Program // Referred by {recId}
                </p>
              )}
              {mode === "register" && (
                <div>
                  <label htmlFor="auth-name" className={labelCls}>Callsign (display name)</label>
                  <input id="auth-name" data-testid="auth-name-input" required minLength={2} maxLength={24} value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="e.g. Rozank" />
                </div>
              )}
              <div>
                <label htmlFor="auth-email" className={labelCls}>Email</label>
                <input id="auth-email" data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="citizen@newcitizen.com" />
              </div>
              <div>
                <label htmlFor="auth-password" className={labelCls}>Password {mode === "register" && "(min 8 chars)"}</label>
                <input id="auth-password" data-testid="auth-password-input" type="password" required minLength={mode === "register" ? 8 : 1} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
              </div>
              {mode === "register" && (
                <>
                  <div>
                    <label htmlFor="auth-dob" className={labelCls}>Date of birth (18+ only)</label>
                    <input id="auth-dob" data-testid="auth-dob-input" type="date" required value={dob} onChange={(e) => setDob(e.target.value)}
                      max={new Date(Date.now() - 18 * 365.25 * 86400000).toISOString().slice(0, 10)}
                      className={`${inputCls} [color-scheme:dark]`} />
                    <p className="font-mono text-[9px] text-zinc-600 mt-1.5 leading-relaxed">New Citizen is strictly adults-only. Your real date of birth is required — a checkbox is not enough.</p>
                  </div>
                  <label data-testid="auth-agree-wrap" className="flex items-start gap-3 cursor-pointer select-none">
                    <input id="auth-agree" data-testid="auth-agree-checkbox" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#2E6BFF] shrink-0" required />
                    <span className="font-mono text-[10px] text-zinc-400 leading-relaxed">
                      I agree to the <Link to="/eula" data-testid="auth-tos-link" className="text-nc-blue underline underline-offset-2">Terms of Service</Link> and{" "}
                      <Link to="/privacy" data-testid="auth-privacy-link" className="text-nc-blue underline underline-offset-2">Privacy Policy</Link>,
                      including the collection of device &amp; browser information for security and fraud prevention.
                    </span>
                  </label>
                  <label data-testid="auth-nc-updates-wrap" className="flex items-start gap-3 cursor-pointer select-none">
                    <input id="auth-nc-updates" data-testid="auth-nc-updates-checkbox" type="checkbox" checked={ncUpdates} onChange={(e) => setNcUpdates(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#2E6BFF] shrink-0" />
                    <span className="font-mono text-[10px] text-zinc-400 leading-relaxed">
                      Keep me in the loop — New Citizen update news, events and announcements. Optional, off by default, and you can change it anytime in your community profile.
                    </span>
                  </label>
                </>
              )}
              {error && (
                <p data-testid="auth-error" className="font-mono text-xs text-red-500 border border-red-500/30 bg-red-500/10 px-3 py-2">{error}</p>
              )}
              <button
                data-testid="auth-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-50 text-white font-sans font-semibold uppercase tracking-[0.2em] text-sm py-3 flex items-center justify-center gap-2 transition-colors duration-300"
              >
                {mode === "register" ? <UserPlus className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
                {loading ? "Processing..." : mode === "register" ? "Enlist Now" : "Login"}
              </button>
              <p className="font-mono text-[10px] text-zinc-600 leading-relaxed">
                Admin & dev accounts are protected by mandatory two-factor authentication.
              </p>
              <Link to="/" data-testid="auth-back-link" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 hover:text-zinc-300 uppercase flex items-center gap-2 transition-colors duration-300">
                <ArrowLeft className="w-3 h-3" /> Back to roadmap
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
